import { getAccessToken } from './session';

const API_BASE = (import.meta.env.VITE_API_URL as string) || '';

async function authHeader(): Promise<Record<string, string>> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
    ...((init.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const contentType = res.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const message = typeof body === 'object' && body && 'error' in (body as Record<string, unknown>)
      ? String((body as Record<string, unknown>).error)
      : `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return body as T;
}

// ─── Types ───────────────────────────────────────────────────────────

export type BusinessVertical = 'trades' | 'restaurant';

export interface Business {
  id: string;
  owner_user_id: string;
  name: string;
  vertical?: BusinessVertical;
  website: string | null;
  industry: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_country: string | null;
  system_prompt: string | null;
  business_profile: BusinessProfile | null;
  ai_voice: string | null;
  greeting: string | null;
  assistant_name: string | null;
  telnyx_phone_number: string | null;
  telnyx_phone_id: string | null;
  telnyx_app_id: string | null;
  telnyx_messaging_profile_id: string | null;
  dispatch_phone: string | null;
  dispatch_email_gateway: string | null;
  owner_email: string | null;
  notify_email_verified_at: string | null;
  notify_email_verify_sent_at: string | null;
  dispatch_self_test_at: string | null;
  dispatch_self_test_result: DispatchSelfTestResult | null;
  phone_strategy: 'direct' | 'forward' | null;
  public_phone_number: string | null;
  transfer_to_human: string | null;
  status: 'draft' | 'provisioning' | 'live' | 'paused';
  onboarding_step: number;
  created_at: string;
  updated_at: string;

  // Enrichment side-channels (populated by /api/onboarding/scrape + smart-start)
  brand?: BusinessBrand | null;
  trust_score?: number | null;
  trust_flags?: TrustFlag[] | null;
  trust_review_required?: boolean;
  booking_platform?: string | null;
  booking_url?: string | null;
  contact_phones?: ContactPhone[] | null;
  contact_emails?: ContactEmail[] | null;
  service_area_cities?: string[] | null;
  service_area_polygon?: { type: 'Polygon'; coordinates: number[][][] } | null;
  owner_sop?: string | null;
  last_crawled_at?: string | null;
  next_recrawl_at?: string | null;
  profile_version?: number;

  square_connected?: boolean;
  square_status?: string;
  square_location_name?: string | null;
  square_catalog_item_count?: number;
  square?: SquareStatus;
}

export interface SquareLocation {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
}

export interface SquareStatus {
  configured: boolean;
  connected: boolean;
  status: 'disconnected' | 'pending_location' | 'active' | 'error';
  merchant_id?: string | null;
  location_id?: string | null;
  location_name?: string | null;
  catalog_item_count?: number;
  catalog_synced_at?: string | null;
  last_error?: string | null;
  environment?: 'sandbox' | 'production';
  server_environment?: 'sandbox' | 'production';
  redirect_uri?: string | null;
  env_mismatch?: boolean;
  config_issue?: string | null;
}

export interface VoiceOrderLineItem {
  variation_id?: string;
  item_name?: string;
  variation_name?: string | null;
  quantity?: number;
  note?: string | null;
  price_cents?: number | null;
  currency?: string;
}

export interface VoiceOrder {
  id: string;
  business_id: string;
  call_log_id: string | null;
  customer_id: string | null;
  square_order_id: string | null;
  call_intent: string;
  status: 'draft' | 'submitted' | 'paid' | 'failed' | 'cancelled';
  total_cents: number | null;
  currency: string | null;
  line_items: VoiceOrderLineItem[];
  error_message: string | null;
  payment_link_url?: string | null;
  payment_link_id?: string | null;
  confirmation_sms_sent_at?: string | null;
  square_payment_id?: string | null;
  paid_at?: string | null;
  created_at: string;
  updated_at: string;
  caller_phone?: string | null;
  call_started_at?: string | null;
  customer?: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone'> | null;
}

export interface BusinessProfile {
  business_name?: string;
  industry?: string;
  tagline?: string | null;
  license_info?: string | null;
  hours?: string;
  service_area?: string[];
  main_phone?: string | null;
  address?: string | null;
  services?: { name: string; description?: string | null; price?: string | null }[];
  pricing_notes?: string | null;
  policies?: string | null;
  average_response_time?: string | null;
  restaurant_payment_mode?: 'pay_at_pickup' | 'payment_link';
  restaurant_order_sms?: boolean;
  _meta?: { provenance?: Record<string, ProvenanceEntry>; built_at?: string };
}

export interface ProvenanceEntry {
  source: 'owner_sop' | 'owner_edit' | 'jsonld' | 'places' | 'firecrawl_extract' | 'gemini_inference' | 'manual' | 'contacts_canonical' | 'merged' | string;
  confidence: number;
  last_seen?: string;
  agreement?: 'all' | 'partial' | 'mismatch' | 'single';
  item_count?: number;
}

export interface BusinessBrand {
  logo_url?: string | null;
  hero_url?: string | null;
  screenshot_url?: string | null;
  palette?: string[];
  tone_words?: string[];
  visual_signals?: string[];
}

export interface TrustFlag {
  code: string;
  severity: 'info' | 'warn' | 'high';
  message: string;
}

export interface ContactPhone {
  e164: string;
  national?: string;
  source_url?: string | null;
  role?: string;
}

export interface ContactEmail {
  email: string;
  role: string;
  source_url?: string | null;
}

export interface ProfileConflict {
  id?: string;
  field: string;
  options: { source: string; value: unknown }[];
  recommendation?: string | null;
  created_at?: string;
}

export interface BookingDetection {
  id: string;
  label: string;
  booking_url: string | null;
  intake_fields: string[];
  confidence: number;
  evidence_count: number;
}

export interface ServiceAreaSummary {
  cities: string[];
  polygon: boolean;
}

export interface EnrichmentResponse {
  profile: BusinessProfile;
  provenance: Record<string, ProvenanceEntry>;
  conflicts: ProfileConflict[];
  sources: Record<string, unknown>;
  contacts: {
    phones: ContactPhone[];
    emails: ContactEmail[];
    canonical_phone: { e164: string; national: string; agreement: string } | null;
  };
  booking: BookingDetection | null;
  service_area: { cities: string[]; geocoded: { name: string; lat: number; lng: number }[]; polygon: { type: 'Polygon'; coordinates: number[][][] } | null };
  brand: BusinessBrand;
  trust: { score: number | null; flags: TrustFlag[]; review_required: boolean };
  crawl: { pages: string[]; used_seconds: number; source: 'firecrawl' | 'fallback' };
  source_pages: string[];
}

export interface KnowledgeHit {
  text: string;
  section: string | null;
  source: string;
  source_url: string | null;
  similarity: string;
}

export interface Voice {
  name: string;
  sample_line: string;
}

export interface JobDispatch {
  id: string;
  business_id: string;
  service_type: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  address: string | null;
  service_needed: string | null;
  urgency: string | null;
  sms_sent: boolean;
  created_at: string;
}

// ─── CRM ─────────────────────────────────────────────────────────────

export interface Technician {
  id: string;
  business_id: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  employment_type: 'employee' | 'subcontractor';
  hourly_rate_cents: number | null;
  commission_pct: number; // 0..100
  is_active: boolean;
  is_on_duty?: boolean;
  last_known_lat?: number | null;
  last_known_lng?: number | null;
  last_seen_at?: string | null;
  // Phase 5 — 4-layer dispatch readiness per tech.
  carrier?: string | null;
  email_gateway?: string | null;
  email_welcome_sent_at?: string | null;
  email_welcome_bounced_at?: string | null;
  dispatch_consent_at?: string | null;
  dispatch_consent_source?: 'owner_attested' | 'tech_double_optin' | null;
  magic_token?: string | null;
  erpnext_employee_id?: string | null;
  erpnext_sales_person?: string | null;
  created_at: string;
  updated_at: string;
}

export type LayerStatus = 'ok' | 'fail' | 'timeout' | 'queued' | 'skipped';

export interface DispatchSelfTestResult {
  ran_at: string;
  /** User-facing: text to dispatch phone (SMS + carrier fallback). */
  layer_text?: LayerStatus;
  /** User-facing: backup email to owner. */
  layer_email?: LayerStatus;
  layer1_telnyx: LayerStatus;
  layer2_email_sms: LayerStatus;
  layer3_owner_email: LayerStatus;
  layer4_in_app: LayerStatus;
  overall: 'sms' | 'email' | 'in_app_only';
  error?: string | null;
  /** Telnyx message id — used to poll queued → delivered */
  telnyx_message_id?: string | null;
  delivered_at?: string | null;
}

export interface Customer {
  id: string;
  business_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  email: string | null;
  property_type: string | null;
  vehicle_info: string | null;
  notes: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  total_jobs: number;
  total_calls: number;
  last_contact_at: string | null;
  twenty_person_id: string | null;
  twenty_synced_at: string | null;
  erpnext_customer_id: string | null;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export type StageKey = 'new' | 'scheduled' | 'enroute' | 'in_progress' | 'completed' | 'lost';

export interface PipelineStage {
  id: string;
  business_id: string;
  key: StageKey | string;
  label: string;
  color: string;
  sort_order: number;
  is_terminal: boolean;
  created_at: string;
}

export type DispatchStatus =
  | 'pending'
  | 'delivered_telnyx'
  | 'delivered_email_sms'
  | 'delivered_email'
  | 'delivered_app_only';

export interface DeliveryAttempt {
  channel: string;     // 'telnyx' | 'email-primary' | 'email-mms' | 'email-sms' | 'owner-email'
  target: string | null;
  status: 'ok' | 'fail' | 'skipped';
  detail: string | null;
}

export interface Job {
  id: string;
  business_id: string;
  customer_id: string | null;
  call_log_id: string | null;
  dispatch_id: string | null;
  title: string;
  service_type: string | null;
  service_needed: string | null;
  urgency: string | null;
  property_type: string | null;
  vehicle_info: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  address_lat?: number | null;
  address_lng?: number | null;
  stage_key: StageKey | string;
  status: 'open' | 'won' | 'lost';
  dispatch_status: DispatchStatus | null;
  delivery_attempts: DeliveryAttempt[] | null;
  assignee: string | null;
  scheduled_for: string | null;
  completed_at: string | null;
  quoted_price_cents: number | null;
  final_price_cents: number | null;
  currency: string | null;
  twenty_opportunity_id: string | null;
  twenty_synced_at: string | null;
  tags: string[];
  notes: string | null;
  raw_payload: Record<string, unknown> | null;
  // ERPNext integration — captured on close, mirrored to a Sales Invoice + Payment Entry.
  primary_tech_id: string | null;
  // Surfaced by the Pipeline endpoint when a job has techs assigned.
  assigned_techs?: Array<{
    id: string;
    first_name: string;
    last_name: string | null;
    phone: string | null;
    role: 'lead' | 'helper';
    revenue_share_pct: number | null;
  }> | null;
  payment_method: string | null;
  payment_reference: string | null;
  payment_received_at: string | null;
  /** Who received money from the customer — drives settlement reports. */
  payment_collected_by: 'company' | 'tech' | null;
  parts_cost_cents: number | null;
  parts_paid_by: 'tech' | 'company' | null;
  erpnext_invoice_id?: string | null;
  erpnext_payment_id?: string | null;
  created_at: string;
  updated_at: string;
  customer?: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone'> | null;
}

export interface Notification {
  id: string;
  business_id: string;
  job_id: string | null;
  customer_id: string | null;
  call_log_id: string | null;
  severity: 'info' | 'warn' | 'critical';
  type: 'job_dispatched' | 'dispatch_partial' | 'dispatch_failed' | 'call_started' | 'call_failed' | 'error' | string;
  title: string;
  body: string | null;
  metadata: Record<string, unknown> & { attempts?: DeliveryAttempt[]; dispatch_status?: DispatchStatus };
  read_at: string | null;
  created_at: string;
  job_title?: string | null;
  job_dispatch_status?: DispatchStatus | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  customer_phone?: string | null;
}

export type ActivityType =
  | 'call_started' | 'call_ended' | 'job_created' | 'voice_order'
  | 'sms_sent' | 'sms_failed' | 'sms_received'
  | 'stage_changed' | 'note' | 'assigned' | 'scheduled' | 'twenty_synced';

export interface Activity {
  id: string;
  business_id: string;
  customer_id: string | null;
  job_id: string | null;
  call_log_id: string | null;
  type: ActivityType | string;
  title: string | null;
  body: string | null;
  metadata: Record<string, unknown>;
  actor: string | null;
  created_at: string;
  customer?: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone'> | null;
  job?: Pick<Job, 'id' | 'title' | 'stage_key'> | null;
}

export interface CallLog {
  id: string;
  business_id: string;
  call_control_id: string | null;
  caller_phone: string | null;
  direction: string | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  ended_reason: string | null;
  transcript_summary: string | null;
}

export interface GmbCandidate {
  place_id: string;
  name: string;
  address: string;
  formatted_address: string;
  primary_type: string | null;
  types: string[];
  rating: number | null;
  reviews_count: number | null;
  status: string | null;
  lat: number | null;
  lng: number | null;
  website: string | null;
  phone: string | null;
  maps_url: string | null;
}

export interface GmbPlace {
  place_id: string;
  name: string | null;
  address: string | null;
  phone: string | null;
  website: string | null;
  maps_url: string | null;
  rating: number | null;
  reviews_count: number | null;
  primary_type: string | null;
  status: string | null;
  opening_hours: string[];
}

export interface NumberSuggestion {
  phone_number: string;
  national_format: string;
  locality: string | null;
  state: string | null;
  area_code: string;
  upfront_cost: string | null;
  monthly_cost: string | null;
  currency: string;
}

export interface OwnedNumber {
  phone_number: string;
  national_format: string;
  area_code: string;
  purchased_at: string | null;
  telnyx_status: string;
  connection_id: string | null;
  available: boolean;
  currently_assigned_to_business: boolean;
}

export interface CarrierDetection {
  provider_key: 'verizon' | 'att' | 't-mobile' | 'sprint' | 'us-cellular' | 'cricket' | 'google-voice' | 'voip' | 'unknown';
  provider_label: string;
  type: string;
  gateway: string | null;
  gateway_options: string[];
  notice: string | null;
}

export interface CarrierLookup {
  phone: string;
  national_format?: string;
  ok: boolean;
  raw_carrier?: { name: string | null; type: string | null };
  detection: CarrierDetection;
  reason?: string;
}

export interface ErpNextConfigStatus {
  configured: boolean;
  enabled?: boolean;
  company?: string | null;
  status?: 'pending' | 'ready' | 'disabled';
  provisioned_at?: string | null;
  last_sync_at?: string | null;
  last_sync_error?: string | null;
  last_synced_event?: string | null;
}

export interface ErpNextPingResult {
  ok: boolean;
  error?: string;
  base_url?: string;
  company?: string | null;
  bench_user?: string | null;
  status?: number | null;
}

export interface ErpNextEmployee {
  name: string;
  employee_name?: string;
  first_name?: string;
  last_name?: string;
  status?: string;
  cell_number?: string;
  personal_email?: string;
  image?: string;
}

export interface ErpNextSalesPerson {
  name: string;
  sales_person_name: string;
  commission_rate?: number;
  enabled?: number;
}

export interface ErpNextInvoice {
  name: string;
  customer: string;
  customer_name?: string;
  posting_date: string;
  grand_total: number;
  outstanding_amount: number;
  status: string;
  currency: string;
}

export interface ErpNextOutboxEvent {
  id: string;
  event_type: string;
  status: 'pending' | 'delivered' | 'failed';
  attempt: number;
  http_status: number | null;
  response_excerpt: string | null;
  created_at: string;
  last_attempted_at: string | null;
}

// ─── Analytics (native — replaces Superset/Metabase) ────────────────

export interface LeaderboardRow {
  technician_id: string;
  tech_name: string;
  avatar_url: string | null;
  jobs_completed: number;
  jobs_won: number;
  jobs_lost: number;
  revenue_won_cents: number;
  avg_ticket_cents: number;
  commission_earned_cents: number;
  commission_paid_cents: number;
  hours_logged: number | string | null;
  last_job_at: string | null;
}

export interface RevenueDay {
  day: string;
  won_cents: number;
  billed_cents: number;
  collected_cents: number;
}

export interface ArAgingRow {
  customer_id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  open_invoices: number;
  bucket_0_30: number;
  bucket_30_60: number;
  bucket_60_90: number;
  bucket_90_plus: number;
  total_due: number;
}

export interface ArAgingTotals {
  bucket_0_30?: number;
  bucket_30_60?: number;
  bucket_60_90?: number;
  bucket_90_plus?: number;
  total_due?: number;
}

export interface FunnelDay {
  day: string;
  calls: number;
  jobs_created: number;
  jobs_won: number;
}

export interface ServiceMixRow {
  service_type: string;
  jobs: number;
  wins: number;
  revenue_cents: number;
}

export interface SourceAttributionRow {
  source: string;
  jobs: number;
  wins: number;
  revenue_cents: number;
  win_rate_pct: number | null;
}

export interface CommissionPayrollRow {
  technician_id: string;
  tech_name: string;
  status: string;
  payroll_period_start: string | null;
  payroll_period_end: string | null;
  job_count: number;
  basis_cents: number;
  commission_cents: number;
  paid_at: string | null;
}

export type SettlementPeriod = 'week' | 'biweek' | 'month' | 'year';

export interface TechRoutingRecommendation {
  technician_id: string;
  tech_name: string;
  distance_miles?: number | null;
  distance_label?: string;
  eta_minutes?: number;
  eta_label?: string;
  eta_source?: 'google' | 'estimate';
  location_source?: 'live' | 'last_job';
  location_label?: string;
  is_on_duty?: boolean;
  is_recommended?: boolean;
  unavailable?: boolean;
  reason?: string;
  dispatch_consent_at?: string | null;
}

export interface JobRoutingResponse {
  job_location: { lat: number; lng: number; address: string; geocoded_at?: string } | null;
  geocode_error?: string;
  recommendations: TechRoutingRecommendation[];
}

export interface SettlementSummary {
  jobs_closed: number;
  job_total_cents: number;
  net_labor_cents?: number;
  tech_commission_cents: number;
  company_share_cents: number;
  employer_owes_tech_cents: number;
  techs_owe_company_cents: number;
  parts_reimburse_cents?: number;
  parts_recovery_cents?: number;
  jobs_company_collected: number;
  jobs_tech_collected: number;
}

export interface SettlementTechRow {
  technician_id: string;
  tech_name: string;
  tech_email?: string | null;
  jobs: number;
  tech_commission_cents: number;
  company_share_cents: number;
  employer_owes_tech_cents: number;
  tech_owes_company_cents: number;
  parts_reimburse_cents?: number;
  parts_recovery_cents?: number;
}

export interface SettlementTimelineRow {
  bucket: string;
  jobs: number;
  tech_commission_cents: number;
  company_share_cents: number;
  employer_owes_tech_cents: number;
  techs_owe_company_cents: number;
}

export interface JobSettlementRow {
  job_id: string;
  title: string;
  completed_at: string;
  gross_cents?: number;
  final_price_cents?: number;
  parts_cost_cents?: number | null;
  parts_paid_by?: 'tech' | 'company' | null;
  net_basis_cents?: number;
  payment_method: string | null;
  payment_collected_by: 'company' | 'tech' | null;
  technician_id: string;
  tech_name: string;
  share_pct?: number;
  commission_pct?: number;
  tech_commission_cents: number;
  company_share_cents: number;
  parts_reimburse_cents?: number | null;
  parts_recovery_cents?: number | null;
  open_balance_cents?: number;
  settlement_direction: 'employer_pays_tech' | 'tech_remits_company' | null;
  commission_status?: string | null;
  paid_at?: string | null;
}

export interface SettlementReport {
  period: SettlementPeriod;
  anchor: string;
  bounds: { start: string; end: string; label: string; granularity: string } | null;
  summary: SettlementSummary;
  by_tech: SettlementTechRow[];
  timeline: SettlementTimelineRow[];
  jobs: JobSettlementRow[];
  schema_pending?: boolean;
}

// ─── Workflows (native rules engine — replaces n8n) ─────────────────

export type WorkflowOp =
  | 'eq' | 'neq' | 'gte' | 'lte' | 'gt' | 'lt'
  | 'contains' | 'in' | 'exists' | 'not_exists';

export interface WorkflowCondition {
  field: string;
  op: WorkflowOp;
  value?: unknown;
}

export type WorkflowActionType =
  | 'send_sms' | 'create_notification' | 'create_activity'
  | 'update_job' | 'tag_customer';

export interface WorkflowAction {
  type: WorkflowActionType | string;
  params: Record<string, unknown>;
}

export interface Workflow {
  id: string;
  business_id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  trigger_event: string;
  conditions: WorkflowCondition[];
  actions: WorkflowAction[];
  template_slug: string | null;
  last_run_at: string | null;
  last_run_status: string | null;
  last_run_error: string | null;
  run_count: number;
  created_at: string;
  updated_at: string;
}

export interface WorkflowTrigger {
  value: string;
  label: string;
}

export interface WorkflowTemplate {
  slug: string;
  name: string;
  description: string;
  trigger_event: string;
  conditions: WorkflowCondition[];
  actions: WorkflowAction[];
  category: string;
}

export interface WorkflowRunStep {
  type: string;
  params: Record<string, unknown>;
  ok: boolean;
  output?: unknown;
  error?: string | null;
}

export interface WorkflowRun {
  id: string;
  workflow_id: string;
  workflow_name?: string;
  business_id: string;
  event_type: string;
  event_payload: Record<string, unknown>;
  status: 'pending' | 'ok' | 'error' | 'skipped';
  matched: boolean;
  error: string | null;
  step_output: WorkflowRunStep[];
  started_at: string;
  finished_at: string | null;
}

// ─── Inbox (native — replaces Chatwoot) ─────────────────────────────

export type MessageChannel = 'sms' | 'email' | 'call' | 'note';
export type MessageDirection = 'inbound' | 'outbound';
export type MessageStatus = 'queued' | 'sending' | 'sent' | 'delivered' | 'failed' | 'received';

export interface InboxMessage {
  id: string;
  business_id: string;
  customer_id: string | null;
  job_id: string | null;
  call_log_id: string | null;
  channel: MessageChannel;
  direction: MessageDirection;
  from_addr: string | null;
  to_addr: string | null;
  body: string;
  status: MessageStatus;
  error: string | null;
  provider: string | null;
  provider_message_id: string | null;
  created_at: string;
  delivered_at: string | null;
}

export interface InboxThreadSummary {
  customer_id: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  last_channel: MessageChannel;
  last_direction: MessageDirection;
  last_body: string;
  last_at: string;
  unread_count: number;
  message_count: number;
}

// ─── Bookings (native — replaces Cal.com) ───────────────────────────

export interface BookingAvailabilityRule {
  day: number;       // 0 = Sunday … 6 = Saturday
  start: string;     // "HH:MM"
  end: string;       // "HH:MM"
}

export interface BookingLink {
  id: string;
  business_id: string;
  slug: string;
  name: string;
  description: string | null;
  duration_minutes: number;
  buffer_minutes: number;
  advance_notice_hours: number;
  max_per_day: number;
  timezone: string;
  availability: BookingAvailabilityRule[];
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface BookingSlot {
  id: string;
  business_id: string;
  link_id: string;
  customer_id: string | null;
  job_id: string | null;
  starts_at: string;
  ends_at: string;
  guest_name: string | null;
  guest_phone: string | null;
  guest_email: string | null;
  notes: string | null;
  status: 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  cancelled_at: string | null;
  cancel_reason: string | null;
  source: 'web' | 'ai_call' | 'manual';
  metadata: Record<string, unknown>;
  created_at: string;
  link_name?: string;
  link_slug?: string;
  customer?: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone' | 'email'> | null;
}

export interface PublicBookingLinkSummary {
  link: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    duration_minutes: number;
    timezone: string;
    advance_notice_hours: number;
  };
  business: { name: string };
  available_slots: string[];
}

// ─── Documents (native e-sign — replaces Documenso) ─────────────────

export interface DocumentLineItem {
  description: string;
  qty?: number;
  unit_cents?: number;
  total_cents?: number;
}

export interface DocumentSigner {
  id: string;
  document_id: string;
  signer_name: string | null;
  signer_email: string | null;
  signer_phone: string | null;
  role: string;
  status: 'pending' | 'viewed' | 'signed' | 'declined';
  first_viewed_at: string | null;
  signed_at: string | null;
  declined_at: string | null;
  decline_reason: string | null;
  signature_name: string | null;
  signature_data_url: string | null;
  sign_url?: string;
  created_at: string;
}

export interface DocumentRow {
  id: string;
  business_id: string;
  customer_id: string | null;
  job_id: string | null;
  number: string;
  title: string;
  kind: 'quote' | 'estimate' | 'contract' | string;
  status: 'draft' | 'sent' | 'viewed' | 'signed' | 'declined' | 'expired' | 'void';
  line_items: DocumentLineItem[];
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  content: string | null;
  terms: string | null;
  sent_at: string | null;
  signed_at: string | null;
  expires_at: string | null;
  promoted_invoice_id: string | null;
  signer_count?: number;
  signed_count?: number;
  customer?: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone' | 'email'> | null;
  job?: Pick<Job, 'id' | 'title' | 'stage_key'> | null;
  signers?: DocumentSigner[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PublicDocumentView {
  document: {
    id: string;
    number: string;
    title: string;
    kind: string;
    content: string | null;
    terms: string | null;
    line_items: DocumentLineItem[];
    subtotal_cents: number;
    tax_cents: number;
    total_cents: number;
    currency: string;
    status: string;
    expires_at: string | null;
  };
  business: { name: string; phone: string | null; email: string | null };
  signer: {
    id: string;
    name: string | null;
    role: string;
    status: string;
    first_viewed_at: string | null;
    signed_at: string | null;
  };
}

// ─── Grid (spreadsheet — replaces NocoDB) ───────────────────────────

export type GridResource = 'customers' | 'jobs' | 'invoices';

export interface GridFilter {
  field: string;
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'contains';
  value: unknown;
}

export interface GridSort {
  field: string;
  dir: 'asc' | 'desc';
}

export interface GridView {
  id: string;
  business_id: string;
  resource: GridResource;
  name: string;
  is_default: boolean;
  filters: GridFilter[];
  sort: GridSort[];
  columns: string[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface GridQueryResult {
  rows: Record<string, unknown>[];
  total: number;
  columns: string[];
  resource: GridResource;
  limit: number;
  offset: number;
}

export interface CrmInsights {
  totals: {
    customers: number;
    jobs: number;
    jobs_won: number;
    jobs_lost: number;
    jobs_30d: number;
    jobs_7d: number;
    calls_30d: number;
    calls_7d: number;
    notifications_unread: number;
    needs_attention: number;
    win_rate: number | null;
    won_revenue_cents: number;
    avg_quoted_cents: number;
    avg_final_cents: number;
    orders_7d?: number;
    orders_30d?: number;
    info_calls_7d?: number;
    missed_calls_7d?: number;
    order_calls_7d?: number;
  };
  stage_rollup: { stage_key: string; count: number; quoted_cents: number; final_cents: number }[];
  jobs_timeseries:  { day: string; count: number }[];
  calls_timeseries: { day: string; count: number }[];
  top_services: { service: string; count: number }[];
  attention: { id: string; title: string; dispatch_status: string | null; created_at: string;
               customer: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone'> | null;
               caller_phone?: string | null; total_cents?: number | null }[];
  top_customers: Pick<Customer, 'id' | 'first_name' | 'last_name' | 'phone' |
                                'total_jobs' | 'total_calls' | 'last_contact_at'>[];
  recent_activity: Activity[];
}

// ─── Platform admin (operator console) ───────────────────────────────

export interface AdminMe {
  is_admin: boolean;
  configured: boolean;
}

export interface AdminUserFlags {
  orphan?: boolean;
  has_business?: boolean;
  live?: boolean;
  stale?: boolean;
  active?: boolean;
  email_confirmed?: boolean;
  auth_missing?: boolean;
}

export interface AdminBusinessSummary {
  id: string;
  name: string;
  status: Business['status'];
  vertical?: BusinessVertical;
  industry?: string | null;
  onboarding_step?: number;
  telnyx_phone_number?: string | null;
  dispatch_phone?: string | null;
  square_connected?: boolean;
  call_count?: number;
  job_count?: number;
  customer_count?: number;
  order_count?: number;
  calls_7d?: number;
  jobs_7d?: number;
  calls_30d?: number;
  jobs_30d?: number;
  last_call_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AdminUserRow {
  id: string;
  email: string | null;
  created_at: string | null;
  email_confirmed_at: string | null;
  last_sign_in_at: string | null;
  provider: string | null;
  business_count: number;
  live_count: number;
  total_calls: number;
  total_jobs: number;
  total_customers: number;
  total_orders: number;
  calls_7d?: number;
  jobs_7d?: number;
  calls_30d?: number;
  jobs_30d?: number;
  last_call_at: string | null;
  first_business_at: string | null;
  flags: AdminUserFlags;
  plan?: string;
  mrr_cents?: number;
  businesses: Pick<AdminBusinessSummary, 'id' | 'name' | 'status' | 'vertical'>[];
}

export interface AdminBilling {
  plan: string;
  mrr_cents: number;
  stripe_customer_id: string | null;
  billing_notes: string | null;
}

export interface AdminUserDetail extends AdminUserRow {
  phone: string | null;
  banned_until: string | null;
  is_anonymous: boolean;
  owner_email: string | null;
  draft_count: number;
  provisioning_count: number;
  paused_count: number;
  with_phone_count: number;
  last_business_at: string | null;
  last_business_updated_at: string | null;
  businesses: AdminBusinessSummary[];
  stripe_customer_id?: string | null;
  billing_notes?: string | null;
}

export interface AdminAuditEntry {
  id: string;
  actor_id: string;
  actor_email: string | null;
  action: string;
  target_user_id: string | null;
  target_business_id: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  created_at: string;
}

export interface AdminAuditList {
  entries: AdminAuditEntry[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface AdminImpersonateResult {
  ok: boolean;
  email: string;
  token_hash: string | null;
  email_otp: string | null;
  verification_type: string;
}

export interface AdminPurgeResult {
  ok: boolean;
  user_id: string;
  businesses_deleted: { id: string; name: string; phone: string | null; phone_pooled: boolean }[];
  phones_released: string[];
  auth_deleted: boolean;
  errors: string[];
}

export interface AdminOverview {
  users: {
    total: number;
    with_business: number;
    orphans: number;
    live_owners: number;
    active: number;
    stale: number;
    unconfirmed: number;
    signups_7d: number;
    signups_30d: number;
  };
  businesses: {
    by_status: { status: string; n: number }[];
    total: number;
  };
  platform: {
    total_calls: number;
    total_jobs: number;
    total_customers: number;
    total_orders: number;
    square_connections: number;
  };
  billing?: {
    total_mrr_cents: number;
    paying_users: number;
    cached_users: number;
  };
  recent_signups: { id: string; email: string | null; created_at: string | null; business_count: number }[];
  recent_businesses: {
    id: string;
    name: string;
    status: string;
    vertical: string;
    owner_user_id: string;
    owner_email: string | null;
    created_at: string;
  }[];
  cache_age_ms: number;
}

export interface AdminUsersList {
  users: AdminUserRow[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// ─── Endpoints ───────────────────────────────────────────────────────

export const api = {
  health: () => request<{ status: string; public_url: string | null }>(`/health`),

  voices: () => request<{ voices: Voice[] }>(`/api/onboarding/voices`),

  carrierGateways: () =>
    request<{ gateways: { value: string; label: string; provider_key: string }[] }>(
      `/api/onboarding/carrier-gateways`,
    ),

  previewVoice: async (voice: string, text?: string): Promise<Blob> => {
    const res = await fetch(`${API_BASE}/api/onboarding/voices/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await authHeader()),
      },
      body: JSON.stringify({ voice, text }),
    });
    if (!res.ok) {
      const contentType = res.headers.get('content-type') || '';
      const body = contentType.includes('application/json') ? await res.json() : await res.text();
      const message = typeof body === 'object' && body && 'error' in (body as Record<string, unknown>)
        ? String((body as Record<string, unknown>).error)
        : `Voice preview failed: ${res.status}`;
      throw new Error(message);
    }
    return res.blob();
  },

  listBusinesses: () => request<{ businesses: Business[] }>(`/api/onboarding/business`),

  createBusiness: (body: Partial<Business>) =>
    request<{ business: Business }>(`/api/onboarding/business`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getBusiness: (id: string) =>
    request<{ business: Business }>(`/api/onboarding/business/${id}`),

  patchBusiness: (id: string, body: Partial<Business>) =>
    request<{ business: Business }>(`/api/onboarding/business/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteBusiness: (id: string, confirm_name: string) =>
    request<{ ok: boolean; released_phone: string | null; phone_pooled?: boolean }>(`/api/onboarding/business/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm_name }),
    }),

  scrape: (body: { website: string; place_id?: string; owner_sop?: string }) =>
    request<EnrichmentResponse>(
      `/api/onboarding/scrape`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  enrich: (business_id: string) =>
    request<{ business: Business; enrichment: EnrichmentResponse }>(
      `/api/onboarding/enrich`,
      { method: 'POST', body: JSON.stringify({ business_id }) },
    ),

  saveSop: (business_id: string, sop: string) =>
    request<{ ok: boolean; indexed: { indexed: number; skipped: number } }>(
      `/api/onboarding/sop`,
      { method: 'POST', body: JSON.stringify({ business_id, sop }) },
    ),

  conflicts: (business_id: string) =>
    request<{ conflicts: ProfileConflict[] }>(
      `/api/onboarding/conflicts/${business_id}`,
    ),

  resolveConflict: (conflict_id: string, value: unknown, source = 'owner_edit') =>
    request<{ ok: boolean }>(
      `/api/onboarding/conflicts/${conflict_id}/resolve`,
      { method: 'POST', body: JSON.stringify({ value, source }) },
    ),

  testKnowledge: (business_id: string, query: string) =>
    request<{ hits: KnowledgeHit[] }>(
      `/api/onboarding/knowledge/lookup`,
      { method: 'POST', body: JSON.stringify({ business_id, query }) },
    ),

  smartStart: (body: {
    source: 'website' | 'gmb' | 'manual';
    vertical?: BusinessVertical;
    business_id?: string;
    name?: string;
    industry?: string;
    assistant_name?: string;
    greeting?: string;
    website?: string;
    place_id?: string;
    description?: string;
    phone?: string;
    address_street?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
  }) =>
    request<{
      business: Business;
      profile: BusinessProfile;
      derived_phone: string | null;
      enrichment?: {
        provenance: Record<string, ProvenanceEntry>;
        conflicts: ProfileConflict[];
        booking: BookingDetection | null;
        brand: BusinessBrand;
        trust: { score: number | null; flags: TrustFlag[]; review_required: boolean };
        contacts: { phones: ContactPhone[]; emails: ContactEmail[]; canonical_phone: { e164: string; national: string; agreement: string } | null };
        service_area: ServiceAreaSummary;
      } | null;
    }>(
      `/api/onboarding/smart-start`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  gmbSearch: (query: string, near?: string) =>
    request<{ candidates: GmbCandidate[] }>(`/api/onboarding/gmb/search`, {
      method: 'POST',
      body: JSON.stringify({ query, near: near || '' }),
    }),

  gmbAnalyze: (body: { place_id: string; hinted_industry?: string; address_fallback?: string }) =>
    request<{ profile: BusinessProfile; place: GmbPlace; source_summary: string }>(
      `/api/onboarding/gmb/analyze`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  compilePrompt: (body: {
    profile: BusinessProfile;
    owner_overrides?: string;
    assistant_name?: string;
    business_name: string;
    business_id?: string;
  }) =>
    request<{ system_prompt: string }>(`/api/onboarding/compile-prompt`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  suggestNumbers: (business_id: string, area_code?: string) =>
    request<{ basis: string; numbers: NumberSuggestion[] }>(`/api/onboarding/suggest-numbers`, {
      method: 'POST',
      body: JSON.stringify({ business_id, area_code: area_code || undefined }),
    }),

  ownedNumbers: () =>
    request<{ numbers: OwnedNumber[] }>(`/api/onboarding/owned-numbers`),

  provision: (body: {
    business_id: string;
    phone_number?: string;
    area_code?: string;
    locality?: string;
    phone_strategy?: 'direct' | 'forward';
    public_phone_number?: string;
  }) =>
    request<{ business: Business }>(`/api/onboarding/provision`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  lookupCarrier: (phone: string) =>
    request<CarrierLookup>(`/api/onboarding/lookup-carrier`, {
      method: 'POST',
      body: JSON.stringify({ phone }),
    }),

  // ─── Layer 3 — notify-email verification ─────────────────────────
  resendNotifyEmail: (business_id: string) =>
    request<{ ok: boolean }>(`/api/onboarding/notify-email/resend`, {
      method: 'POST', body: JSON.stringify({ business_id }),
    }),

  // ─── Dispatch self-test (text + backup email) ─────────────
  dispatchSelfTest: (business_id: string) =>
    request<{
      ok: boolean;
      stamp: DispatchSelfTestResult;
      attempts: Array<{ channel: string; target: string | null; status: string; detail: string | null }>;
      sms_sent: boolean;
      email_sent: boolean;
      dispatch_status: string;
    }>(`/api/onboarding/dispatch-self-test`, {
      method: 'POST', body: JSON.stringify({ business_id }),
    }),

  watchDispatchSelfTest: (business_id: string) =>
    request<{
      ok: boolean;
      updated: boolean;
      stamp: DispatchSelfTestResult;
      status: string;
      error?: string;
    }>(`/api/onboarding/dispatch-self-test/watch`, {
      method: 'POST', body: JSON.stringify({ business_id }),
    }),

  refreshWebhooks: (business_id: string) =>
    request<{ ok: boolean; public_url: string }>(`/api/onboarding/refresh-webhooks`, {
      method: 'POST',
      body: JSON.stringify({ business_id }),
    }),

  stats: (id: string) =>
    request<{
      total_calls: number;
      total_jobs: number;
      total_orders?: number;
      recent_jobs: JobDispatch[];
    }>(`/api/onboarding/business/${id}/stats`),

  square: {
    status: (businessId: string) =>
      request<{ square: SquareStatus }>(`/api/square/status/${businessId}`),

    connectUrl: (businessId: string, returnTo?: string) =>
      request<{ url: string; redirect_uri: string; environment?: 'sandbox' | 'production' }>(
        `/api/square/connect/${businessId}${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ''}`,
      ),

    locations: (businessId: string) =>
      request<{ locations: SquareLocation[] }>(`/api/square/locations/${businessId}`),

    selectLocation: (businessId: string, location_id: string) =>
      request<{ location: SquareLocation; square: SquareStatus }>(`/api/square/select-location`, {
        method: 'POST',
        body: JSON.stringify({ business_id: businessId, location_id }),
      }),

    syncCatalog: (businessId: string) =>
      request<{ item_count: number; synced_at: string; square: SquareStatus }>(`/api/square/sync-catalog`, {
        method: 'POST',
        body: JSON.stringify({ business_id: businessId }),
      }),

    disconnect: (businessId: string) =>
      request<{ ok: boolean; square: SquareStatus }>(`/api/square/disconnect`, {
        method: 'POST',
        body: JSON.stringify({ business_id: businessId }),
      }),
  },

  // ─── CRM ────────────────────────────────────────────────────────────
  crm: {
    insights: (businessId: string) =>
      request<CrmInsights>(`/api/crm/${businessId}/insights`),

    orders: (businessId: string) =>
      request<{ orders: VoiceOrder[] }>(`/api/crm/${businessId}/orders`),

    pipeline: (businessId: string) =>
      request<{ stages: PipelineStage[]; jobs: Job[] }>(`/api/crm/${businessId}/pipeline`),

    listCustomers: (businessId: string, q?: string) =>
      request<{ customers: Customer[] }>(
        `/api/crm/${businessId}/customers${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),

    createCustomer: (businessId: string, body: Partial<Customer>) =>
      request<{ customer: Customer }>(`/api/crm/${businessId}/customers`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    getCustomer: (businessId: string, customerId: string) =>
      request<{ customer: Customer; jobs: Job[]; activities: Activity[]; calls: CallLog[] }>(
        `/api/crm/${businessId}/customers/${customerId}`,
      ),

    patchCustomer: (businessId: string, customerId: string, body: Partial<Customer>) =>
      request<{ customer: Customer }>(`/api/crm/${businessId}/customers/${customerId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    listJobs: (businessId: string, params: { stage?: string; status?: string; q?: string } = {}) => {
      const qs = new URLSearchParams();
      if (params.stage)  qs.set('stage', params.stage);
      if (params.status) qs.set('status', params.status);
      if (params.q)      qs.set('q', params.q);
      const tail = qs.toString();
      return request<{ jobs: Job[] }>(`/api/crm/${businessId}/jobs${tail ? `?${tail}` : ''}`);
    },

    createJob: (businessId: string, body: Partial<Job> & { customer_phone?: string; customer_name?: string }) =>
      request<{ job: Job }>(`/api/crm/${businessId}/jobs`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    getJob: (businessId: string, jobId: string) =>
      request<{ job: Job & { customer: Customer | null }; activities: Activity[] }>(
        `/api/crm/${businessId}/jobs/${jobId}`,
      ),

    jobRouting: (businessId: string, jobId: string) =>
      request<JobRoutingResponse>(`/api/crm/${businessId}/jobs/${jobId}/routing`),

    patchJob: (businessId: string, jobId: string, body: Partial<Job>) =>
      request<{ job: Job }>(`/api/crm/${businessId}/jobs/${jobId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),

    activities: (businessId: string, type?: string | string[], limit = 100) => {
      const qs = new URLSearchParams();
      if (Array.isArray(type) && type.length) qs.set('types', type.join(','));
      else if (typeof type === 'string' && type !== 'all') qs.set('type', type);
      qs.set('limit', String(limit));
      return request<{ activities: Activity[] }>(`/api/crm/${businessId}/activities?${qs.toString()}`);
    },

    addNote: (businessId: string, body: { customer_id?: string; job_id?: string; title?: string; body?: string }) =>
      request<{ activity: Activity }>(`/api/crm/${businessId}/activities`, {
        method: 'POST',
        body: JSON.stringify({ type: 'note', ...body }),
      }),

    notifications: {
      list: (businessId: string, opts: { unread?: boolean; limit?: number } = {}) => {
        const qs = new URLSearchParams();
        if (opts.unread) qs.set('unread', '1');
        if (opts.limit)  qs.set('limit', String(opts.limit));
        const tail = qs.toString();
        return request<{ notifications: Notification[]; unread_count: number }>(
          `/api/crm/${businessId}/notifications${tail ? `?${tail}` : ''}`,
        );
      },

      markRead: (businessId: string, id: string) =>
        request<{ notification: Notification | null }>(
          `/api/crm/${businessId}/notifications/${id}/read`,
          { method: 'POST' },
        ),

      markAllRead: (businessId: string) =>
        request<{ ok: boolean }>(
          `/api/crm/${businessId}/notifications/read-all`,
          { method: 'POST' },
        ),
    },

    failedDispatchJobs: (businessId: string) =>
      request<{ jobs: Job[] }>(`/api/crm/${businessId}/jobs/failed-dispatch`),

    analytics: {
      leaderboard: (businessId: string) =>
        request<{ rows: LeaderboardRow[] }>(`/api/crm/${businessId}/analytics/leaderboard`),

      revenue: (businessId: string, days = 90) =>
        request<{ rows: RevenueDay[] }>(`/api/crm/${businessId}/analytics/revenue?days=${days}`),

      arAging: (businessId: string) =>
        request<{ rows: ArAgingRow[]; totals: ArAgingTotals }>(
          `/api/crm/${businessId}/analytics/ar-aging`,
        ),

      funnel: (businessId: string, days = 30) =>
        request<{ rows: FunnelDay[] }>(`/api/crm/${businessId}/analytics/funnel?days=${days}`),

      serviceMix: (businessId: string) =>
        request<{ rows: ServiceMixRow[] }>(`/api/crm/${businessId}/analytics/service-mix`),

      sourceAttribution: (businessId: string) =>
        request<{ rows: SourceAttributionRow[] }>(
          `/api/crm/${businessId}/analytics/source-attribution`,
        ),

      commissionPayroll: (businessId: string, params?: { period?: 'week' | 'month' | 'year'; anchor?: string }) => {
        const qs = new URLSearchParams();
        if (params?.period) qs.set('period', params.period);
        if (params?.anchor) qs.set('anchor', params.anchor);
        const tail = qs.toString();
        return request<{ rows: CommissionPayrollRow[] }>(
          `/api/crm/${businessId}/analytics/commission-payroll${tail ? `?${tail}` : ''}`,
        );
      },

      settlement: (businessId: string, params: { period: SettlementPeriod; anchor?: string }) => {
        const qs = new URLSearchParams({ period: params.period });
        if (params.anchor) qs.set('anchor', params.anchor);
        return request<SettlementReport>(
          `/api/crm/${businessId}/analytics/settlement?${qs}`,
        );
      },

      settlementExportUrl: (
        businessId: string,
        params: {
          period: SettlementPeriod;
          anchor?: string;
          format?: 'html' | 'csv';
          inline?: boolean;
          technicianId?: string | null;
        },
      ) => {
        const qs = new URLSearchParams({ period: params.period, format: params.format || 'html' });
        if (params.anchor) qs.set('anchor', params.anchor);
        if (params.inline) qs.set('inline', '1');
        if (params.technicianId) qs.set('technician_id', params.technicianId);
        return `${API_BASE}/api/crm/${businessId}/analytics/settlement/export?${qs}`;
      },

      emailSettlement: (
        businessId: string,
        body: {
          period: SettlementPeriod;
          anchor?: string;
          audience: 'owner' | 'techs' | 'all' | 'tech';
          technicianId?: string;
        },
      ) =>
        request<{
          ok: boolean;
          sent?: Array<{ email: string; role: string; name?: string }>;
          skipped?: Array<{ role: string; name?: string; reason: string; email?: string }>;
          error?: string;
        }>(`/api/crm/${businessId}/analytics/settlement/email`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
    },

    workflows: {
      list: (businessId: string) =>
        request<{ workflows: Workflow[]; triggers: WorkflowTrigger[]; supported_actions: string[] }>(
          `/api/crm/${businessId}/workflows`,
        ),

      templates: (businessId: string) =>
        request<{ templates: WorkflowTemplate[]; triggers: WorkflowTrigger[] }>(
          `/api/crm/${businessId}/workflows/templates`,
        ),

      runs: (businessId: string, limit = 50) =>
        request<{ runs: WorkflowRun[] }>(`/api/crm/${businessId}/workflows/runs?limit=${limit}`),

      get: (businessId: string, id: string) =>
        request<{ workflow: Workflow; runs: WorkflowRun[] }>(
          `/api/crm/${businessId}/workflows/${id}`,
        ),

      create: (businessId: string, body: Partial<Workflow> & { from_template?: string }) =>
        request<{ workflow: Workflow }>(`/api/crm/${businessId}/workflows`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),

      patch: (businessId: string, id: string, body: Partial<Workflow>) =>
        request<{ workflow: Workflow }>(`/api/crm/${businessId}/workflows/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),

      remove: (businessId: string, id: string) =>
        request<{ ok: true }>(`/api/crm/${businessId}/workflows/${id}`, { method: 'DELETE' }),

      runOnce: (businessId: string, id: string, payload: Record<string, unknown> = {}) =>
        request<{ result: { status: string; matched: boolean; steps?: WorkflowRunStep[] } }>(
          `/api/crm/${businessId}/workflows/${id}/run`,
          { method: 'POST', body: JSON.stringify({ payload }) },
        ),
    },

    inbox: {
      threads: (businessId: string) =>
        request<{ threads: InboxThreadSummary[] }>(`/api/crm/${businessId}/inbox/threads`),

      thread: (businessId: string, customerId: string) =>
        request<{ customer: Customer | null; messages: InboxMessage[] }>(
          `/api/crm/${businessId}/inbox/threads/${customerId}`,
        ),

      send: (businessId: string, body: { customer_id: string; body: string; channel?: MessageChannel }) =>
        request<{ message: InboxMessage }>(`/api/crm/${businessId}/inbox/send`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),

      markRead: (businessId: string, customerId: string) =>
        request<{ ok: true }>(`/api/crm/${businessId}/inbox/threads/${customerId}/read`, {
          method: 'POST',
        }),
    },

    bookings: {
      links: (businessId: string) =>
        request<{ links: BookingLink[] }>(`/api/crm/${businessId}/bookings/links`),

      createLink: (businessId: string, body: Partial<BookingLink>) =>
        request<{ link: BookingLink }>(`/api/crm/${businessId}/bookings/links`, {
          method: 'POST', body: JSON.stringify(body),
        }),

      getLink: (businessId: string, id: string) =>
        request<{ link: BookingLink; available_slots: string[] }>(
          `/api/crm/${businessId}/bookings/links/${id}`,
        ),

      patchLink: (businessId: string, id: string, body: Partial<BookingLink>) =>
        request<{ link: BookingLink }>(`/api/crm/${businessId}/bookings/links/${id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),

      deleteLink: (businessId: string, id: string) =>
        request<{ ok: true }>(`/api/crm/${businessId}/bookings/links/${id}`, {
          method: 'DELETE',
        }),

      list: (businessId: string, opts: { upcoming?: boolean; limit?: number } = {}) => {
        const qs = new URLSearchParams();
        if (opts.upcoming) qs.set('upcoming', '1');
        if (opts.limit)    qs.set('limit', String(opts.limit));
        const tail = qs.toString();
        return request<{ bookings: BookingSlot[] }>(
          `/api/crm/${businessId}/bookings${tail ? `?${tail}` : ''}`,
        );
      },

      cancel: (businessId: string, slotId: string, reason?: string) =>
        request<{ booking: BookingSlot }>(
          `/api/crm/${businessId}/bookings/${slotId}/cancel`,
          { method: 'POST', body: JSON.stringify({ reason: reason || null }) },
        ),
    },

    documents: {
      list: (businessId: string, status?: string) => {
        const tail = status ? `?status=${encodeURIComponent(status)}` : '';
        return request<{ documents: DocumentRow[] }>(`/api/crm/${businessId}/documents${tail}`);
      },

      create: (businessId: string, body: Partial<DocumentRow> & {
        line_items?: DocumentLineItem[];
        signers?: { customer_id?: string; signer_name?: string; signer_email?: string; signer_phone?: string; role?: string }[];
      }) =>
        request<{ document: DocumentRow }>(`/api/crm/${businessId}/documents`, {
          method: 'POST', body: JSON.stringify(body),
        }),

      get: (businessId: string, id: string) =>
        request<{ document: DocumentRow }>(`/api/crm/${businessId}/documents/${id}`),

      patch: (businessId: string, id: string, body: Partial<DocumentRow>) =>
        request<{ document: DocumentRow }>(`/api/crm/${businessId}/documents/${id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),

      remove: (businessId: string, id: string) =>
        request<{ ok: true }>(`/api/crm/${businessId}/documents/${id}`, { method: 'DELETE' }),

      send: (businessId: string, id: string, body: { channel?: string; message?: string } = {}) =>
        request<{ ok: boolean; sent_to: string[]; sign_urls: string[] }>(
          `/api/crm/${businessId}/documents/${id}/send`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
    },

    grid: {
      listViews: (businessId: string, resource?: GridResource) => {
        const tail = resource ? `?resource=${resource}` : '';
        return request<{ views: GridView[] }>(`/api/crm/${businessId}/grid/views${tail}`);
      },

      createView: (businessId: string, body: Partial<GridView>) =>
        request<{ view: GridView }>(`/api/crm/${businessId}/grid/views`, {
          method: 'POST', body: JSON.stringify(body),
        }),

      patchView: (businessId: string, id: string, body: Partial<GridView>) =>
        request<{ view: GridView }>(`/api/crm/${businessId}/grid/views/${id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),

      deleteView: (businessId: string, id: string) =>
        request<{ ok: true }>(`/api/crm/${businessId}/grid/views/${id}`, {
          method: 'DELETE',
        }),

      query: (businessId: string, body: {
        resource: GridResource;
        filters?: GridFilter[];
        sort?: GridSort[];
        columns?: string[];
        limit?: number;
        offset?: number;
      }) =>
        request<GridQueryResult>(`/api/crm/${businessId}/grid/query`, {
          method: 'POST', body: JSON.stringify(body),
        }),

      bulk: (businessId: string, body: {
        resource: GridResource;
        ids: string[];
        patch: Record<string, unknown>;
      }) =>
        request<{ updated: number; ids?: string[] }>(`/api/crm/${businessId}/grid/bulk`, {
          method: 'POST', body: JSON.stringify(body),
        }),
    },

    technicians: {
      list: (businessId: string) =>
        request<{ technicians: Technician[] }>(`/api/crm/${businessId}/technicians`),
      create: (businessId: string, body: Partial<Technician> & { consent?: boolean }) =>
        request<{ technician: Technician }>(`/api/crm/${businessId}/technicians`, {
          method: 'POST', body: JSON.stringify(body),
        }),
      patch: (businessId: string, techId: string, body: Partial<Technician> & { consent?: boolean }) =>
        request<{ technician: Technician }>(`/api/crm/${businessId}/technicians/${techId}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),
      remove: (businessId: string, techId: string) =>
        request<{ ok: boolean }>(`/api/crm/${businessId}/technicians/${techId}`, {
          method: 'DELETE',
        }),
      resendWelcome: (businessId: string, techId: string) =>
        request<{ ok: boolean }>(`/api/crm/${businessId}/technicians/${techId}/resend-welcome`, {
          method: 'POST',
        }),
      assignToJob: (businessId: string, jobId: string, body: { technician_id: string; role?: 'lead' | 'helper'; revenue_share_pct?: number; notify?: boolean }) =>
        request<{ job: Job; tech: Technician; dispatch_result: { smsSent: boolean; emailSent: boolean; dispatchStatus: string } | null }>(
          `/api/crm/${businessId}/jobs/${jobId}/assign`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
      unassignFromJob: (businessId: string, jobId: string, techId: string) =>
        request<{ ok: boolean }>(`/api/crm/${businessId}/jobs/${jobId}/assign/${techId}`, {
          method: 'DELETE',
        }),
    },

    erpnext: {
      config: (businessId: string) =>
        request<ErpNextConfigStatus>(`/api/crm/${businessId}/erpnext/config`),

      ping: (businessId: string) =>
        request<ErpNextPingResult>(`/api/crm/${businessId}/erpnext/ping`, { method: 'POST' }),

      disable: (businessId: string) =>
        request<{ ok: boolean }>(`/api/crm/${businessId}/erpnext/disable`, { method: 'POST' }),

      employees: (businessId: string) =>
        request<{ employees: ErpNextEmployee[] }>(`/api/crm/${businessId}/erpnext/employees`),

      salesPersons: (businessId: string) =>
        request<{ sales_persons: ErpNextSalesPerson[] }>(`/api/crm/${businessId}/erpnext/sales-persons`),

      payrollSummary: (businessId: string, params?: { from?: string; to?: string }) => {
        const qs = new URLSearchParams();
        if (params?.from) qs.set('from', params.from);
        if (params?.to)   qs.set('to', params.to);
        const suffix = qs.toString();
        return request<{ invoices: ErpNextInvoice[]; from: string; to: string }>(
          `/api/crm/${businessId}/erpnext/payroll-summary${suffix ? `?${suffix}` : ''}`,
        );
      },

      backfill: (businessId: string) =>
        request<{ ok: boolean; customers: number; jobs: number }>(
          `/api/crm/${businessId}/erpnext/backfill`, { method: 'POST' },
        ),

      outbox: (businessId: string) =>
        request<{ events: ErpNextOutboxEvent[] }>(`/api/crm/${businessId}/erpnext/outbox`),

      pushTech: (businessId: string, techId: string) =>
        request<{ ok: boolean }>(
          `/api/crm/${businessId}/erpnext/push-tech/${techId}`, { method: 'POST' },
        ),
    },
  },

  // ─── Platform admin (operator console — server-gated) ───────────────
  admin: {
    me: () => request<AdminMe>('/api/admin/me'),

    overview: () => request<AdminOverview>('/api/admin/overview'),

    listUsers: (params?: {
      page?: number;
      per_page?: number;
      q?: string;
      filter?: 'all' | 'orphan' | 'has_business' | 'live' | 'active' | 'stale' | 'unconfirmed';
      sort?: 'created_at' | 'email' | 'last_sign_in_at' | 'business_count' | 'total_calls';
      refresh?: boolean;
    }) => {
      const qs = new URLSearchParams();
      if (params?.page) qs.set('page', String(params.page));
      if (params?.per_page) qs.set('per_page', String(params.per_page));
      if (params?.q) qs.set('q', params.q);
      if (params?.filter && params.filter !== 'all') qs.set('filter', params.filter);
      if (params?.sort) qs.set('sort', params.sort);
      if (params?.refresh) qs.set('refresh', '1');
      const suffix = qs.toString();
      return request<AdminUsersList>(`/api/admin/users${suffix ? `?${suffix}` : ''}`);
    },

    getUser: (userId: string) =>
      request<{ user: AdminUserDetail }>(`/api/admin/users/${encodeURIComponent(userId)}`),

    refresh: () =>
      request<{ ok: boolean; count: number; platform_users_synced?: number; refreshed_at: string }>(
        '/api/admin/refresh',
        { method: 'POST' },
      ),

    listAudit: (params?: { page?: number; per_page?: number }) => {
      const qs = new URLSearchParams();
      if (params?.page) qs.set('page', String(params.page));
      if (params?.per_page) qs.set('per_page', String(params.per_page));
      const suffix = qs.toString();
      return request<AdminAuditList>(`/api/admin/audit${suffix ? `?${suffix}` : ''}`);
    },

    updateBilling: (userId: string, body: Partial<AdminBilling>) =>
      request<{ ok: boolean; billing: AdminBilling }>(
        `/api/admin/users/${encodeURIComponent(userId)}/billing`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),

    banUser: (userId: string) =>
      request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(userId)}/ban`, { method: 'POST' }),

    unbanUser: (userId: string) =>
      request<{ ok: boolean }>(`/api/admin/users/${encodeURIComponent(userId)}/unban`, { method: 'POST' }),

    resetPassword: (userId: string) =>
      request<{ ok: boolean }>(
        `/api/admin/users/${encodeURIComponent(userId)}/reset-password`,
        { method: 'POST', body: JSON.stringify({}) },
      ),

    resendConfirmation: (userId: string) =>
      request<{ ok: boolean }>(
        `/api/admin/users/${encodeURIComponent(userId)}/resend-confirmation`,
        { method: 'POST', body: JSON.stringify({}) },
      ),

    impersonate: (userId: string) =>
      request<AdminImpersonateResult>(
        `/api/admin/users/${encodeURIComponent(userId)}/impersonate`,
        { method: 'POST', body: JSON.stringify({}) },
      ),

    purgeUser: (userId: string, body: { confirm_email: string; delete_auth?: boolean }) =>
      request<AdminPurgeResult>(
        `/api/admin/users/${encodeURIComponent(userId)}/purge`,
        { method: 'POST', body: JSON.stringify(body) },
      ),

    downloadUsersCsv: async (params?: {
      q?: string;
      filter?: string;
      sort?: string;
    }) => {
      const qs = new URLSearchParams();
      if (params?.q) qs.set('q', params.q);
      if (params?.filter && params.filter !== 'all') qs.set('filter', params.filter);
      if (params?.sort) qs.set('sort', params.sort);
      const suffix = qs.toString();
      const headers = await authHeader();
      const res = await fetch(`${API_BASE}/api/admin/users/export.csv${suffix ? `?${suffix}` : ''}`, {
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(typeof body.error === 'string' ? body.error : `Export failed: ${res.status}`);
      }
      return res.blob();
    },
  },

  // ─── Public / unauthenticated ─────────────────────────────────────
  public: {
    booking: (slug: string) =>
      publicRequest<PublicBookingLinkSummary>(`/api/public/bookings/${encodeURIComponent(slug)}`),

    book: (slug: string, body: {
      starts_at: string;
      guest_name: string;
      guest_phone?: string;
      guest_email?: string;
      notes?: string;
    }) =>
      publicRequest<{ booking: { id: string; starts_at: string; ends_at: string;
                                  link_name: string; business_name: string } }>(
        `/api/public/bookings/${encodeURIComponent(slug)}/book`,
        { method: 'POST', body: JSON.stringify(body) },
      ),

    document: (token: string) =>
      publicRequest<PublicDocumentView>(`/api/public/sign/${encodeURIComponent(token)}`),

    sign: (token: string, body: { signer_name: string; signature_data_url: string }) =>
      publicRequest<{ ok: boolean; already_signed?: boolean; promoted_invoice_id?: string | null }>(
        `/api/public/sign/${encodeURIComponent(token)}/accept`,
        { method: 'POST', body: JSON.stringify(body) },
      ),

    decline: (token: string, reason?: string) =>
      publicRequest<{ ok: boolean }>(`/api/public/sign/${encodeURIComponent(token)}/decline`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason || null }),
      }),
  },
};

// Auth-free request helper for the public booking + sign endpoints.
async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) || {}),
  };
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const contentType = res.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const message = typeof body === 'object' && body && 'error' in (body as Record<string, unknown>)
      ? String((body as Record<string, unknown>).error)
      : `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return body as T;
}
